import { Account } from "@/models/Account";
import { Movimiento } from "@/models/Movimiento";
import { Saving } from "@/models/Saving";
import Toast from "@/plugins/Toast";
import AccountsRepository from "@/repositories/AccountsRepository";
import SavingRepository from "@/repositories/SavingRepository";
import PdfService from "@/services/PdfService";
import ToolsService from "@/services/ToolsService";
import { useAuthStore } from "@/store/auth.store";
import { useSavingStore } from "@/store/saving.store";
import { ToastType } from "@/types/toast";
import dayjs from "dayjs";
import "dayjs/locale/es"; // Importa el idioma español
import { computed, ref, inject } from "vue";
import { useRouter } from "vue-router";

export default class SavingDetailViewModel {
  public breadcrumbs = [
    { title: "Dashboard", disabled: false, href: "/app/home" },
    { title: "Detalle Ahorro", disabled: true, href: "" },
  ];
  public authStore = useAuthStore();
  public savingStore = useSavingStore();
  public saving = computed(() => this.savingStore.selectedSaving as Saving);
  public selectedDate = ref<{ year: string; month: string } | null>(null);
  public availableDates = ref<
    { label: string; value: { year: string; month: string } }[]
  >([]);
  public loadingCronograma = ref(false);
  public loadingEstadoCuenta = ref(false);
  public loadingTransfer = ref(false);
  public loadingMovements = ref(false);

  public accounts = ref<Account>();
  public movements = ref<Movimiento[]>([]);
  private transferDialogRef: any;
  private stateOfAccountDialogRef: any;

  private toolsService = new ToolsService();
  private pdfService = new PdfService();
  private toast: Toast;
  private router = useRouter();

  constructor(transferDialogRef: any, stateOfAccountDialogRef: any) {
    this.toast = inject("toast") as Toast;
    this.transferDialogRef = transferDialogRef;
    this.stateOfAccountDialogRef = stateOfAccountDialogRef;
    this.loadAccounts();
    // Validar si hay un ahorro seleccionado al inicializar
    if (!this.saving.value) {
      this.toast.showToast(
        "No se encontró un ahorro seleccionado.",
        ToastType.Error
      );
      this.router.push("/app/home"); // Redirige al home si no hay ahorro
    } else {
      this.movements.value = Array.isArray(this.saving.value?.movimientos)
        ? this.saving.value.movimientos
        : [];
    }
    this.generateAvailableDates();
  }
  /**
   * Generar una lista de fechas disponibles (últimos 24 meses).
   */
  private generateAvailableDates() {
    dayjs.locale("es");
    const currentDate = dayjs();
    const dates = [];

    for (let i = 0; i < 24; i++) {
      const date = currentDate.subtract(i, "month");
      dates.push({
        label: date.format("MMMM YYYY"), // Esto ahora estará en español, por ejemplo, "enero 2025"
        value: {
          year: date.format("YYYY"),
          month: date.format("MM"),
        },
      });
    }

    this.availableDates.value = dates;
  }
  public async loadAccounts() {
    const authBasic = btoa(
      `${this.authStore.getLogin}:${this.authStore.getDecryptedPassword}`
    );
    try {
      const res = await AccountsRepository.getSocioAccounts(authBasic);
      if (res.data) {
        this.accounts.value = res.data.data;
      }
    } catch (error) {
      console.error("Error al cargar cuentas del socio:", error);
    }
  }

  /**
   * Obtener estado de cuenta y generar PDF.
   * @param date Fecha seleccionada (año y mes).
   */
  public getStateOfAccount = async () => {
    if (!this.selectedDate.value) {
      this.toast.showToast("Seleccione una fecha", ToastType.Info);
      return;
    }

    this.loadingEstadoCuenta.value = true;

    try {
      const authBasic = btoa(
        `${this.authStore.getLogin}:${this.authStore.getDecryptedPassword}`
      );
      const { year, month } = this.selectedDate.value;
      console.log(`Generando estado de cuenta para ${month}-${year}`);
      const formData = new FormData();

      formData.append("year", year);
      formData.append("month", month);
      formData.append("idahorro", this.saving.value.idahorro);

      const response = await SavingRepository.getEstadoAhorro(
        authBasic,
        formData
      );
      console.log(response);
      this.loadingEstadoCuenta.value = false;

      if (response.data.length > 0) {
        /**abrimos el dialog para mostrar la data */
        this.stateOfAccountDialogRef.value.openDialog(
          this.saving.value,
          response.data
        );
      } else {
        this.toast.showToast(
          "No hay datos disponibles en la fecha seleccionada",
          ToastType.Info
        );
      }
    } catch (error) {
      this.loadingEstadoCuenta.value = false;
      this.toast.showToast(
        "No se pudo obtener el estado de cuenta",
        ToastType.Error
      );
      console.error("Error al obtener el estado de cuenta:", error);
    }
  };

  /**
   * Formatear moneda.
   */
  formatCoin(coin: number): string {
    return this.toolsService.getCoin(coin);
  }

  /**
   * Formatear números en decimales.
   */
  formatFloat(number: number): string {
    return this.toolsService.getFloat(number);
  }

  /**
   * Formatear fechas.
   */
  formatDate(date: string | null): string {
    return date ? this.toolsService.formatDate(date) : "---";
  }

  /**
   * Obtener color según el tipo de movimiento.
   */
  getColor(type: string): string {
    return type === "1" ? "success" : type === "2" ? "error" : "primary";
  }

  /**
   * Obtener ícono según el tipo de movimiento.
   */
  getIconName(type: string): string {
    return type === "1"
      ? "mdi-arrow-top-right"
      : type === "2"
      ? "mdi-arrow-bottom-left"
      : "mdi-wallet";
  }

  /**
   * Exportar cronograma de ahorros como PDF.
   */
  async exportCronograma() {
    if (
      !Array.isArray(this.saving.value?.cronogramas) ||
      !this.saving.value.cronogramas.length
    ) {
      this.toast.showToast(
        "No hay cronogramas disponibles para descargar.",
        ToastType.Info
      );
      return;
    }

    this.loadingCronograma.value = true;

    try {
      await this.pdfService.exportSavingSchedule(
        this.saving.value,
        this.authStore.getUserData
      );
      this.toast.showToast(
        "Cronograma descargado correctamente.",
        ToastType.Success
      );
      this.loadingCronograma.value = false;
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      this.toast.showToast("Error al generar el cronograma.", ToastType.Error);
      this.loadingCronograma.value = false;
    }
  }

  /**
   * Solicitar transferencia de ahorros.
   */
  solicitarTransferencia = () => {
    /*     const mockAccounts = [
      {
        idbancocuenta: 1,
        nom: "Cuenta Principal",
        moneda: 1, // S/.
      },
      {
        idbancocuenta: 2,
        nom: "Cuenta Secundaria",
        moneda: 2, // $.
      },
    ];
    this.transferDialogRef.value?.openDialog(mockAccounts); */
    this.transferDialogRef.value?.openDialog(this.accounts.value);
  };
}
